import { apiClient } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import {
  API_EDI_SSCC_SETTINGS,
  API_EDI_SSCC_SETTING,
  API_EDI_SSCC_SETTING_RANGES,
  API_EDI_SSCC_RANGE,
  API_EDI_SSCC_RANGE_SUSPEND,
  API_EDI_SSCC_RANGE_MOVE,
  API_EDI_PACKING_SAVE_ACTUAL,
} from '../../constants/apiConstants';

// ============================================================================
// SSCC settings admin service — Spec mlinkone-sscc-allocation.
// All endpoints return the standard Result<T> envelope. A setting covers a SET
// of factory receiver-codes (ShareCode) or is the catch-all; each has 0..N
// ranges (company prefix + P1/P2 + sequence window).
// ============================================================================

interface ResultEnvelope<T> {
  succeeded: boolean;
  statusCode?: number;
  messages?: string[];
  data?: T;
}

export interface SsccRangeDto {
  id: number;
  ssccSettingId: number;
  matchKind: string;         // vendor | receiver | default
  receiverCode?: string | null;
  companyPrefix: string;
  extensionDigit: string;
  prefix1: string;
  prefix2: string;
  seqStart: number;
  seqEnd: number;
  seqCurrent: number;
  rangeOrder: number;
  source: string;
  rangeStatus: string;
  remainingCapacity: number;
}

export interface SsccSettingDto {
  id: number;
  buyerPartyId: number;
  shareCode?: string | null;
  isCatchAll: boolean;
  refillMode: string;
  isActive: boolean;
  supplierPartyIds: number[];
  ranges: SsccRangeDto[];
}

export interface SaveSsccSettingRequest {
  id?: number;
  tenantId: number;
  buyerPartyId: number;
  shareCode?: string | null;
  isCatchAll: boolean;
  refillMode: string;
  supplierPartyIds: number[];
}

export interface SaveSsccRangeRequest {
  id?: number;
  tenantId: number;
  matchKind: string;         // vendor | receiver | default
  receiverCode?: string | null;
  companyPrefix: string;
  extensionDigit: string;
  prefix1: string;
  prefix2: string;
  seqStart: number;
  seqEnd: number;
  rangeOrder: number;
}

/** Thrown on a BE validation failure, carrying Result.messages verbatim. */
export class SsccSaveError extends Error {
  constructor(public readonly messages: string[]) {
    super(messages.join(', ') || 'Failed to save SSCC setting');
    this.name = 'SsccSaveError';
  }
}

function unwrap<T>(env: ResultEnvelope<T>, fallbackMsg: string): T {
  if (!env.succeeded) throw new SsccSaveError(env.messages ?? [fallbackMsg]);
  return env.data as T;
}

export const ssccService = {
  async list(tenantId: number, buyerPartyId?: number, supplierPartyId?: number): Promise<SsccSettingDto[]> {
    try {
      const q = new URLSearchParams({ tenantId: String(tenantId) });
      if (buyerPartyId) q.set('buyerPartyId', String(buyerPartyId));
      if (supplierPartyId) q.set('supplierPartyId', String(supplierPartyId));
      const res = await apiClient.get<ResultEnvelope<{ settings: SsccSettingDto[]; canManage: boolean }>>(
        `${API_EDI_SSCC_SETTINGS}?${q.toString()}`,
      );
      return unwrap(res.data, 'Failed to load SSCC settings').settings ?? [];
    } catch (error) {
      logger.error('Failed to list SSCC settings', error);
      throw error;
    }
  },

  async saveSetting(request: SaveSsccSettingRequest): Promise<number> {
    const isUpdate = !!request.id && request.id > 0;
    const res = isUpdate
      ? await apiClient.put<ResultEnvelope<{ id: number }>>(API_EDI_SSCC_SETTING(request.id!), request)
      : await apiClient.post<ResultEnvelope<{ id: number }>>(API_EDI_SSCC_SETTINGS, request);
    return unwrap(res.data, 'Failed to save SSCC setting').id;
  },

  async deactivateSetting(id: number, tenantId: number): Promise<void> {
    const res = await apiClient.delete<ResultEnvelope<{ ok: boolean }>>(`${API_EDI_SSCC_SETTING(id)}?tenantId=${tenantId}`);
    unwrap(res.data, 'Failed to deactivate SSCC setting');
  },

  async saveRange(settingId: number, request: SaveSsccRangeRequest): Promise<number> {
    const isUpdate = !!request.id && request.id > 0;
    const res = isUpdate
      ? await apiClient.put<ResultEnvelope<{ id: number }>>(API_EDI_SSCC_RANGE(request.id!), request)
      : await apiClient.post<ResultEnvelope<{ id: number }>>(API_EDI_SSCC_SETTING_RANGES(settingId), request);
    return unwrap(res.data, 'Failed to save SSCC range').id;
  },

  async moveRange(rangeId: number, moveUp: boolean, tenantId: number): Promise<void> {
    const res = await apiClient.post<ResultEnvelope<{ ok: boolean }>>(
      API_EDI_SSCC_RANGE_MOVE(rangeId), { rangeId, moveUp, tenantId },
    );
    unwrap(res.data, 'Failed to reorder SSCC range');
  },

  async deleteRange(rangeId: number, tenantId: number): Promise<void> {
    const res = await apiClient.delete<ResultEnvelope<{ ok: boolean }>>(`${API_EDI_SSCC_RANGE(rangeId)}?tenantId=${tenantId}`);
    unwrap(res.data, 'Failed to delete SSCC range');
  },

  async suspendRange(rangeId: number, suspend: boolean, tenantId: number): Promise<void> {
    const res = await apiClient.post<ResultEnvelope<{ ok: boolean }>>(
      API_EDI_SSCC_RANGE_SUSPEND(rangeId), { rangeId, suspend, tenantId },
    );
    unwrap(res.data, 'Failed to update SSCC range');
  },

  async saveActualPacking(packingListId: number, tenantId: number): Promise<{ assignedCount: number; idType: string }> {
    const res = await apiClient.post<ResultEnvelope<{ packingListId: number; assignedCount: number; idType: string }>>(
      `${API_EDI_PACKING_SAVE_ACTUAL(packingListId)}?tenantId=${tenantId}`, {},
    );
    const data = unwrap(res.data, 'Failed to save actual packing');
    return { assignedCount: data.assignedCount, idType: data.idType };
  },
};
